import { 
  Suspense, lazy, useEffect, useState, useCallback 
} from 'react';
import { 
  ArrowUp, Menu, X, Sun, Moon
} from 'lucide-react';
import { useScrollTracking, useTypewriter } from '../../hooks/useOptimizedHooks';
import { skills, projects, typewriterTexts, certifications } from '../../data/portfolioData';
import LoadingSpinner from '../ui/LoadingSpinner';
import { setupScrolling, preventBodyScroll, smoothScrollToElement } from '../../utils/scrollUtils';
import ScrollTransitions from '../animations/ScrollTransitions';
import HeroSection from '../sections/HeroSection';
import AboutSection from '../sections/AboutSection';
import SkillsSection from '../sections/SkillsSection';
import ProjectsSection from '../sections/ProjectsSection';
import CertificatesSection from '../sections/CertificatesSection';
import Footer from './Footer';

// Lazy load heavy components for better performance
const ContactSection = lazy(() => import('../sections/ContactSection'));

const Portfolio = () => {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isScrolled, activeSection, setActiveSection } = useScrollTracking();
  const currentTypeText = useTypewriter(typewriterTexts, 80);
  // Navigation menu items
  const navigationItems = [
    { id: 'hero', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'skills', label: 'Skills' },
    { id: 'projects', label: 'Projects' },
    { id: 'certificates', label: 'Certifications' },
    { id: 'contact', label: 'Contact' }
  ];

  // Theme switcher
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  }, [theme]);

  // Apply theme and setup scrolling on mount
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    return setupScrolling();
  }, [theme]);

  // Handle mobile menu open/close
  useEffect(() => {
    preventBodyScroll(isMobileMenuOpen);
  }, [isMobileMenuOpen]);  // Scroll to target section with smooth animation
  const handleNavClick = useCallback((sectionId) => {
    setIsMobileMenuOpen(false); // Close mobile menu if open
    const targetElement = document.getElementById(sectionId);
    if (targetElement) {
      // Add a small delay to ensure UI states are updated
      requestAnimationFrame(() => {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(sectionId);
      });
    }
  }, [setActiveSection]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-950 dark:to-neutral-900 text-neutral-900 dark:text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/80 dark:bg-neutral-900/80 backdrop-blur-lg shadow-lg' : ''
      }`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <button 
              onClick={() => handleNavClick('hero')}
              className="text-xl font-bold bg-gradient-to-r from-violet-600 to-blue-600 bg-clip-text text-transparent"
            >
              MS
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navigationItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`text-sm font-medium transition-colors ${
                    activeSection === item.id 
                      ? 'text-violet-600 dark:text-violet-400' 
                      : 'text-neutral-600 dark:text-neutral-400 hover:text-violet-600 dark:hover:text-violet-400'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden transition-all duration-300 ${
          isMobileMenuOpen 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}>
          <div className="px-6 py-4 space-y-4 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800">
            {navigationItems.map(item => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`block w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSection === item.id 
                    ? 'bg-violet-500/10 text-violet-600 dark:text-violet-400' 
                    : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                }`}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={toggleTheme}
              className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
              {theme === 'dark' ? (
                <>
                  <Sun className="w-5 h-5" />
                  <span>Light Mode</span>
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5" />
                  <span>Dark Mode</span>
                </>
              )}
            </button>
          </div>
        </div>
      </nav>      {/* Main Content */}      <ScrollTransitions>
        <HeroSection currentTypeText={currentTypeText} onScrollToProjects={() => handleNavClick('projects')} />
        <AboutSection />
        <SkillsSection skills={skills} />
        <ProjectsSection projects={projects} />
        <CertificatesSection certifications={certifications} />
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        }>
          <ContactSection />
        </Suspense>
      </ScrollTransitions>

      {/* Scroll to top button */}
      <button
        onClick={(e) => {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
          // Small delay to update active section after scroll completes
          setTimeout(() => setActiveSection('hero'), 100);
        }}
        className={`fixed bottom-8 right-8 p-3 rounded-full bg-violet-600 hover:bg-violet-500 text-white shadow-lg transition-all duration-300 transform hover:scale-110 ${
          isScrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        aria-label="Scroll to top"
      >
        <ArrowUp className="w-5 h-5" />
      </button>

      <Footer />
    </div>
  );
};

export default Portfolio;
